import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import "@fontsource/material-icons";
import {
  AppBar,
  Box,
  CssBaseline,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import { useGetCurrentUserQuery } from "../../store/apis/UserApis";
import useNotifier from "../../utils/useNotifier";
import { BaseApi } from "store/apis/BaseApi";
import { BaseMain } from "./styled/BaseMain";
import { DrawerHeader } from "./styled/DrawerHeader";
import useMsalAccount from "utils/useMsalAccount";
import MenuIcon from "@mui/icons-material/Menu";
import RefreshIcon from "@mui/icons-material/Refresh";
import SideDrawer from "./SideDrawer";
import { selectMealName } from "store/slices/orderSlice/OrderSlice";
import DiningRDIcon from "../../assets/imgs/icon512.png";
import { AccountHoldModal } from "components/shared/accountHoldModal/AccountHoldModal";

export default function Base() {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { userId } = useMsalAccount();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const currentMeal = useSelector(selectMealName);
  const currentLocation = useLocation();
  const [isAccountOnHold, setIsAccountOnHold] = useState(false);
  const showMealName = !!(currentLocation.pathname.includes('/takeorder/guest/order') || currentLocation.pathname.includes('/takeorder/residents/order') || currentLocation.pathname.includes('/ordermanagement/residents/order'));
  const [userData, setUserData] = useState(null);

  // Refetch all active queries if the user is changed (using logonAs for example)
  useEffect(() => {
    dispatch(BaseApi.util.resetApiState());
    if (!userId) {
      setUserData(null);
    }
  }, [userId]);

  useEffect(() => {}, [instance?.getActiveAccount()]);

  useNotifier();
  
  const { 
    data: currentUserData,
    isFetching: fetchingUser,
    isSuccess: successUser 
  } = useGetCurrentUserQuery(userId, {
    skip: !isAuthenticated || userId == null,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!fetchingUser && successUser && currentUserData) {
      setIsAccountOnHold(!!currentUserData?.isAccountOnHold); 
      setUserData(currentUserData);
    }
  }, [fetchingUser, successUser]);

  const toggleDrawer = (event, newOpenState) => {
    setOpen(newOpenState);
  }

  return (
    <Box sx={{ display: "flex", width: "100%", height: "100vh" }}>
      <CssBaseline />
      <AppBar 
        id="appHeader" 
        position="fixed" 
        open={open} 
        sx={{
          borderRadius: "10px 10px 0 0 !important", 
          backgroundColor: "var(--tealColor)",
        }}
      >
        <Toolbar sx={{width: "100%", display: "flex"}}>
          <IconButton 
            sx={{color: "#FFF", marginRight: ".25rem", transform: "scale(1.25)"}} aria-label="Menu"
            onClick={(event) => toggleDrawer(event, !open)}>
            <MenuIcon />
          </IconButton>
          <SideDrawer open={open} toggleDrawer={toggleDrawer} />
          <>
            <img 
              src={DiningRDIcon}  
              alt="Dining RD Icon" 
              style={{
                height: "2rem", 
                width: "auto"
              }}
            />
            <Typography 
              variant="h6" 
              component="div" 
              color="#FFF" 
              fontWeight="700"
              marginRight="auto"
              fontFamily="Georgia"
            >
              DiningRD
            </Typography>
          </>
          {
            showMealName && 
            <Typography 
              variant="h6" 
              marginX="auto"
              color="#FFF" 
              fontWeight="700"
              letterSpacing=".5px"
            >
              {currentMeal}
            </Typography> 
          }  
          
          <Box 
            display="flex" 
            flexDirection="column"
            alignItems="flex-end"
            sx={{
              marginRight: "1rem",
              marginLeft: "auto"
            }}  
          >
            <Typography 
              variant="subtitle1" 
              component="div" 
              color="#FFF" 
              fontWeight="700" 
            >
              {userData?.facilityName}
            </Typography>
            <Typography 
              variant="subtitle1" 
              component="div" 
              color="#FFF" 
              fontWeight="400"
            >
              {userData?.firstName} {userData?.lastName} 
            </Typography>
          </Box>
          <IconButton 
            color="inherit"
            sx={{
              color: "var(--tealColor)", 
              border: "1px solid gray", 
              borderRadius: "10px", 
              backgroundColor: "#FFF",
              padding: ".25rem",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, .6)"
              }
            }} 
            aria-label="Refresh"
            onClick={() => window.location.reload()}>
            <RefreshIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <BaseMain>
        <DrawerHeader sx={{minHeight: "40px !important"}} />
        <Outlet context={userData} />
      </BaseMain>
      {isAccountOnHold && <AccountHoldModal open={isAccountOnHold} />}
    </Box>
  );
}
